import React, { useState, useEffect, useCallback } from 'react';
import axios from '../components/services/axiosInstance';
import { FaEye, FaSearch, FaCalendarAlt, FaMoneyBillWave, FaClock, FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import PaymentDetailsModal from '../components/PaymentDetailsModal';
import DailyPaymentPopup from '../components/DailyPaymentPopup';
import './AdminPayments.css';

const AdminPayments = () => {
    const [payments, setPayments] = useState([]);
    const [upcomingPayments, setUpcomingPayments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);

    // Filters & Pagination
    const [statusFilter, setStatusFilter] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        paid: 0,
        remaining: 0
    });

    const fetchPayments = useCallback(async () => {
        try {
            setLoading(true);
            const params = {
                page: currentPage,
                limit: 10,
                status: statusFilter !== 'all' ? statusFilter : undefined,
                search: searchTerm || undefined,
                startDate: startDate || undefined,
                endDate: endDate || undefined
            };

            const response = await axios.get('/payments', { params });
            const allPayments = response.data?.payments || [];
            setPayments(allPayments);
            setTotalPages(response.data?.pagination?.pages || 1);

            // Basic stats calculation (simplified for now)
            // Ideally stats should come from a dedicated endpoint for accuracy across all pages
            const total = allPayments.reduce((acc, p) => acc + (parseFloat(p.totalAmount) || 0), 0);
            const paid = allPayments.reduce((acc, p) => acc + (parseFloat(p.paidAmount) || 0), 0);
            setStats({
                total,
                paid,
                remaining: total - paid
            });

        } catch (error) {
            console.error('Error fetching payments:', error);
            setPayments([]);
        } finally {
            setLoading(false);
        }
    }, [currentPage, statusFilter, searchTerm, startDate, endDate]);

    const fetchUpcomingPayments = async () => {
        try {
            const response = await axios.get('/payments/upcoming');
            setUpcomingPayments(response.data.upcomingPayments);
        } catch (error) {
            console.error('Error fetching upcoming payments:', error);
        }
    };

    useEffect(() => {
        fetchPayments();
        fetchUpcomingPayments();
    }, [fetchPayments]);

    const handleResetFilters = () => {
        setStatusFilter('all');
        setSearchTerm('');
        setStartDate('');
        setEndDate('');
        setCurrentPage(1);
    };

    const getStatusText = (status) => {
        const texts = { active: 'نشط', completed: 'مكتمل', overdue: 'متأخر' };
        return texts[status] || 'نشط';
    };

    const formatDate = (date) => {
        if (!date) return '-';
        return new Date(date).toLocaleDateString('ar-TN', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('ar-TN', { style: 'currency', currency: 'TND' }).format(amount);
    };

    return (
        <div className="admin-payments-container">
            <DailyPaymentPopup
                onPaymentConfirmed={fetchPayments}
                onViewDetails={(payment) => {
                    setSelectedPayment(payment);
                    setShowDetailsModal(true);
                }}
            />

            <header className="payments-header">
                <h1>إدارة المدفوعات</h1>
                <p className="subtitle">لوحة التحكم المالية وتتبع المستحقات</p>
            </header>

            {/* Stats Overview */}
            <div className="payments-stats">
                <div className="stat-card">
                    <div className="stat-icon total"><FaMoneyBillWave /></div>
                    <div className="stat-info">
                        <span className="label">إجمالي المبالغ</span>
                        <span className="value">{formatCurrency(stats.total)}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon paid"><FaCheckCircle /></div>
                    <div className="stat-info">
                        <span className="label">المبالغ المحصلة</span>
                        <span className="value">{formatCurrency(stats.paid)}</span>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon remaining"><FaClock /></div>
                    <div className="stat-info">
                        <span className="label">المبالغ المتبقية</span>
                        <span className="value">{formatCurrency(stats.remaining)}</span>
                    </div>
                </div>
            </div>

            {/* Upcoming Payments List */}
            {upcomingPayments.length > 0 && (
                <section className="upcoming-section">
                    <h2><FaCalendarAlt /> الدفعات القادمة (هذا الأسبوع)</h2>
                    <div className="upcoming-grid">
                        {upcomingPayments.map((inst) => (
                            <div
                                key={inst._id}
                                className="upcoming-card"
                                onClick={() => {
                                    setSelectedPayment(inst.paymentId);
                                    setShowDetailsModal(true);
                                }}
                                style={{ cursor: 'pointer' }}
                            >
                                <div className="upcoming-info">
                                    <h4>{inst.paymentId?.studentId?.username}</h4>
                                    <p>الموعد: {formatDate(inst.dueDate)}</p>
                                </div>
                                <div className="upcoming-val">
                                    {formatCurrency(inst.amount)}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Filters */}
            <div className="filter-controls">
                <div className="controls-row">
                    <div className="filter-item">
                        <label>بحث بالاسم</label>
                        <div className="input-with-icon">
                            <input
                                type="text"
                                className="input-control"
                                placeholder="اسم الطالب..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="filter-item">
                        <label>الحالة</label>
                        <select
                            className="input-control"
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                        >
                            <option value="all">جميع الحالات</option>
                            <option value="active">نشط</option>
                            <option value="completed">مكتمل</option>
                            <option value="overdue">متأخر</option>
                        </select>
                    </div>
                    <div className="filter-item">
                        <label>من تاريخ</label>
                        <input
                            type="date"
                            className="input-control"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                        />
                    </div>
                    <div className="filter-item">
                        <label>إلى تاريخ</label>
                        <input
                            type="date"
                            className="input-control"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                        />
                    </div>
                    <button className="btn-reset" onClick={handleResetFilters}>إعادة تعيين</button>
                </div>
            </div>

            {/* Payments Table */}
            <div className="table-wrapper">
                <table className="payments-table">
                    <thead>
                        <tr>
                            <th>الطالب</th>
                            <th>المبلغ الإجمالي</th>
                            <th>المدفوع</th>
                            <th>المتبقي</th>
                            <th>تاريخ الاستحقاق التالي</th>
                            <th>الحالة</th>
                            <th>إجراءات</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            [...Array(5)].map((_, i) => (
                                <tr key={i}>
                                    <td colSpan="7"><div className="skeleton" style={{ height: '20px', borderRadius: '4px' }}></div></td>
                                </tr>
                            ))
                        ) : payments.length === 0 ? (
                            <tr>
                                <td colSpan="7" className="empty-state">
                                    <FaExclamationTriangle />
                                    <p>لا توجد مدفوعات تتطابق مع معايير البحث</p>
                                </td>
                            </tr>
                        ) : (
                            payments.map((p) => (
                                <tr key={p._id}>
                                    <td
                                        className="student-name clickable"
                                        onClick={() => {
                                            setSelectedPayment(p);
                                            setShowDetailsModal(true);
                                        }}
                                        style={{ cursor: 'pointer', color: '#3498db' }}
                                    >
                                        {p.studentId?.username}
                                    </td>
                                    <td className="amount-text">{formatCurrency(p.totalAmount)}</td>
                                    <td className="amount-text text-success">{formatCurrency(p.paidAmount)}</td>
                                    <td className="amount-text text-warning">{formatCurrency(p.remainingAmount)}</td>
                                    <td>{formatDate(p.nextDueDate)}</td>
                                    <td>
                                        <span className={`status-badge ${p.status}`}>
                                            {getStatusText(p.status)}
                                        </span>
                                    </td>
                                    <td>
                                        <button className="action-btn" onClick={() => {
                                            setSelectedPayment(p);
                                            setShowDetailsModal(true);
                                        }}>
                                            <FaEye />
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>

                {/* Pagination */}
                <div className="pagination-area">
                    <span className="page-number">عرض الصفحة {currentPage} من {totalPages}</span>
                    <div className="pagination-btns">
                        <button
                            className="page-btn"
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(prev => prev - 1)}
                        >السابق</button>
                        <button
                            className="page-btn"
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(prev => prev + 1)}
                        >التالي</button>
                    </div>
                </div>
            </div>

            {showDetailsModal && selectedPayment && (
                <PaymentDetailsModal
                    payment={selectedPayment}
                    onClose={() => {
                        setShowDetailsModal(false);
                        setSelectedPayment(null);
                    }}
                    onUpdate={() => {
                        fetchPayments();
                        fetchUpcomingPayments();
                    }}
                />
            )}
        </div>
    );
};

export default AdminPayments;

